import { Link, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { SVLoader } from '../core';
import { useEffect, useState } from 'react';
import showToast from '@/utils/toast';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import { ROUTES } from '@/constants/routes';
import { useGetUser } from '@/hooks/requests/useUser';
import { useGetFeatureFlags } from '@/hooks/requests/useFeatureFlags';
import { MESSAGES } from '@/constants/messages';
import IdleTimeoutModal from '../IdleTimeoutModal';
import { AppBar, Box, Drawer, IconButton, useMediaQuery } from '@mui/material';
import headerBackgroundImage from '@/images/header-background.png';
import mainBackgroundImage from '@/images/main-background.png';
import darkBackgroundImage from '@/images/auth-background-layout.png';
import Profile from '../Profile';
import HeaderLogo from '../icons/HeaderLogo';
import MainMenu from '../MainMenu';
import MenuIcon from '@mui/icons-material/Menu';
import AdvisorDashboardLogo from '../icons/AdvisorDashboardLogo';
import OwnerSelectModal from '../modals/transactions/OwnerSelectModal';
import TransactionFormModal from '../modals/transactions/TransactionFormModal';
import { useBrandingTheme } from '@/hooks/stores/useBrandingTheme';
import { DarkMode, LightMode } from '@mui/icons-material';

export default function AuthenticatedLayout() {
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { mode, setMode } = useBrandingTheme();
  const isLightMode = mode === 'light';

  const { user, isPending: isPendingUser } = useGetUser();
  const { postLoginScreen, setPostLoginScreen, isStaff } = useAuthUser();
  useGetFeatureFlags();

  useEffect(() => {
    if (!isPendingUser && !user) {
      showToast({ type: 'error', data: { content: MESSAGES.expiredSession } });
    }
  }, [isPendingUser, user]);

  useEffect(() => {
    if (postLoginScreen) {
      setPostLoginScreen('');
      navigate(postLoginScreen);
    }
  }, [postLoginScreen]);

  if (isPendingUser) {
    return <SVLoader isFullScreen />;
  }

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  const showAdvisorDashboardLogo = !isStaff && isLargeScreen;

  return (
    <>
      <OwnerSelectModal />
      <TransactionFormModal />
      <IdleTimeoutModal />
      <AppBar
        sx={{
          backgroundColor: 'black',
          backgroundImage: `url(${headerBackgroundImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          position: 'relative',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isLargeScreen ? '10px 32px' : '10px',
        }}>
        <Link to={ROUTES.HOMEPAGE}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
            }}>
            <HeaderLogo />
            {showAdvisorDashboardLogo && (
              <>
                <Box
                  sx={{
                    borderLeft: '1px solid #a7a9ac',
                    marginRight: 2,
                    marginLeft: 2,
                  }}
                />
                <AdvisorDashboardLogo />
              </>
            )}
          </Box>
        </Link>
        <Box>
          {isLargeScreen ? (
            <>
              <Profile />
              <IconButton
                color="inherit"
                onClick={() => setMode(isLightMode ? 'dark' : 'light')}>
                {isLightMode ? <DarkMode /> : <LightMode />}
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                data-testid="menu-button"
                onClick={() => setOpen(true)}>
                <MenuIcon sx={{ color: 'white' }} />
              </IconButton>
              <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
                <MainMenu setOpen={setOpen} />
              </Drawer>
            </>
          )}
        </Box>
      </AppBar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          minHeight: 'calc(100vh)',
        }}>
        {isLargeScreen && (
          <Box
            sx={{
              width: '280px',
              backgroundColor: isLightMode ? 'white' : '#121212',
              paddingTop: '16px',
            }}>
            <MainMenu setOpen={setOpen} />
          </Box>
        )}
        <Box
          sx={{
            backgroundImage: `url(${isLightMode ? mainBackgroundImage : darkBackgroundImage})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            padding: isLargeScreen ? '40px 32px' : '20px',
            flex: 1,
            overflow: 'hidden',
          }}>
          <Outlet />
        </Box>
      </Box>
    </>
  );
}
